import { useSelector } from 'react-redux';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import React, { useEffect, useState } from 'react';
import { string } from 'prop-types';
import { Helmet } from 'react-helmet-async';

import { LayoutSingleColumn, Page } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import {
  getCatalogProducts,
  getCatalogProduct,
  getCatalogShop,
  getCatalogShops,
  postCatalogInquiry,
} from '../../util/api';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import CatalogShopReviews from '../ListingPage/CatalogShopReviews';

import { categoryLabel } from '../../util/catalogBreadcrumbs';
import { BULK_SHOP_IDS, BULK_TIERS, hasBulkPricing, minUnitPrice } from '../../util/bulkPricing';
import css from './CatalogPage.module.css';

const formatPrice = price =>
  price?.amount != null
    ? new Intl.NumberFormat('en-CA', { style: 'currency', currency: price.currency || 'CAD' }).format(
        price.amount / 100
      )
    : null;

const ProductCard = ({ product }) => (
  <a className={css.productCard} href={`/l/x/${product.id}`}>
    {product.imageUrl ? (
      <img className={css.productImage} src={product.imageUrl} alt={product.title} loading="lazy" />
    ) : (
      <div className={css.productImagePlaceholder} />
    )}
    <div className={css.productBody}>
      <h3 className={css.productTitle}>{product.title}</h3>
      <div className={css.productMeta}>
        {formatPrice(product.price) ? (
          hasBulkPricing(product) ? (
            <span
              className={css.productPrice}
              title={`${formatPrice(product.price)} each · less per set from 5+`}
            >
              From{' '}
              {formatPrice({ ...product.price, amount: minUnitPrice(product.price.amount, product) })}
            </span>
          ) : (
            <span className={css.productPrice}>{formatPrice(product.price)}</span>
          )
        ) : null}
        {product.category ? <span className={css.productCategory}>{categoryLabel(product.category)}</span> : null}
      </div>
    </div>
  </a>
);

const InquiryForm = ({ productId }) => {
  const [values, setValues] = useState({ name: '', contact: '', message: '', neededBy: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const update = field => e => setValues(v => ({ ...v, [field]: e.target.value }));

  const submit = e => {
    e.preventDefault();
    if (!values.name.trim() || !values.contact.trim() || status === 'sending') return;
    setStatus('sending');
    postCatalogInquiry({ productId, ...values })
      .then(() => setStatus('sent'))
      .catch(() => setStatus('error'));
  };

  if (status === 'sent') {
    return (
      <div className={css.inquirySuccess}>
        Request sent! The shop will contact you shortly to confirm details and payment.
      </div>
    );
  }

  return (
    <form className={css.inquiryForm} onSubmit={submit}>
      <h2 className={css.inquiryTitle}>Request this item</h2>
      <p className={css.inquiryHint}>
        Leave your contact and the shop will get back to you to confirm availability, delivery, and
        payment.
      </p>
      <input
        className={css.input}
        placeholder="Your name *"
        value={values.name}
        onChange={update('name')}
        maxLength={120}
        required
      />
      <input
        className={css.input}
        placeholder="Phone or email *"
        value={values.contact}
        onChange={update('contact')}
        maxLength={200}
        required
      />
      <input
        className={css.input}
        placeholder="When do you need it? (optional)"
        value={values.neededBy}
        onChange={update('neededBy')}
        maxLength={100}
      />
      <textarea
        className={css.textarea}
        placeholder="Message: color preferences, delivery address, card note... (optional)"
        value={values.message}
        onChange={update('message')}
        maxLength={2000}
        rows={4}
      />
      <button className={css.submitButton} type="submit" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : 'Send request'}
      </button>
      {status === 'error' ? (
        <p className={css.inquiryError}>Something went wrong — please try again.</p>
      ) : null}
    </form>
  );
};

const BrowseContent = () => {
  const [data, setData] = useState({ products: [], categories: [] });
  const [shops, setShops] = useState([]);
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([getCatalogProducts(category ? { category } : {}), getCatalogShops()])
      .then(([productsRes, shopsRes]) => {
        if (cancelled) return;
        setData({
          products: productsRes.products || [],
          categories: productsRes.categories || data.categories,
        });
        setShops(shopsRes.shops || []);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [category]);

  return (
    <main className={css.content}>
      <section className={css.hero}>
        <h1 className={css.title}>Browse the marketplace</h1>
        <p className={css.subtitle}>
          Fresh flowers, cakes, balloons, and gifts from {shops.length || 'local'} Toronto-area
          shops.
        </p>
      </section>

      <section className={css.section}>
        <div className={css.filterRow}>
          <button
            type="button"
            className={category === '' ? css.filterChipActive : css.filterChip}
            onClick={() => setCategory('')}
          >
            All
          </button>
          {data.categories.map(cat => (
            <button
              key={cat}
              type="button"
              className={category === cat ? css.filterChipActive : css.filterChip}
              onClick={() => setCategory(cat)}
            >
              {categoryLabel(cat)}
            </button>
          ))}
        </div>

        {loading ? (
          <p className={css.loading}>Loading…</p>
        ) : (
          <div className={css.productGrid}>
            {data.products.map(p => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

// Ad traffic for business gifting lands on a shop page with
// ?utm_campaign=corporate… (or ?for=corporate); those visitors get a short
// bulk-order note above the products. Regular visitors see the page as is.
const isCorporateVisit = () => {
  if (typeof window === 'undefined') return false;
  const q = new URLSearchParams(window.location.search);
  return /corporate/i.test(q.get('utm_campaign') || '') || q.get('for') === 'corporate';
};

const CorporateNote = ({ volumePricing }) => (
  <div className={css.corporateNote}>
    <div>
      <h2 className={css.corporateTitle}>Corporate &amp; bulk gift baskets</h2>
      {volumePricing ? (
        <div className={css.corporateTiers}>
          {BULK_TIERS.map(t => (
            <span key={t.minQty} className={css.corporateTier}>
              {t.minQty}+ sets <strong>−{t.pct}%</strong>
            </span>
          ))}
        </div>
      ) : null}
      <p className={css.corporateText}>
        Gifts for clients and your team, delivered across Toronto &amp; the GTA.
        {volumePricing ? ' The discount applies automatically in the cart.' : ''} Need a custom
        message card, a mix of baskets or delivery to several addresses? Contact us and we&rsquo;ll
        put the order together.
      </p>
    </div>
    <div className={css.corporateActions}>
      <a className={css.corporatePrimary} href="tel:+14377742843">
        Call / text +1 437 774 2843
      </a>
      <a className={css.corporateSecondary} href="mailto:info@localflower.ca?subject=Corporate%20gift%20baskets">
        info@localflower.ca
      </a>
    </div>
  </div>
);

const ShopContent = ({ shopId }) => {
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [corporate, setCorporate] = useState(false);

  useEffect(() => setCorporate(isCorporateVisit()), []);

  useEffect(() => {
    let cancelled = false;
    getCatalogShop(shopId)
      .then(res => {
        if (cancelled) return;
        setShop(res.shop);
        setProducts(res.products || []);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [shopId]);

  const scrollToReviews = () => {
    const el = typeof document !== 'undefined' && document.getElementById('reviews');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) return <main className={css.content}><p className={css.loading}>Loading…</p></main>;
  if (!shop) return <main className={css.content}><p className={css.loading}>Shop not found.</p></main>;

  return (
    <main className={css.content}>
      <section className={css.hero}>
        <div className={css.shopHeader}>
          {shop.imageUrl ? (
            <img className={css.shopAvatar} src={shop.imageUrl} alt={shop.displayName} />
          ) : null}
          <div>
            <h1 className={css.title}>{shop.publicData?.storeName || shop.displayName}</h1>
            {shop.address ? <p className={css.subtitle}>{shop.address}</p> : null}
            {shop.reviewCount > 0 && shop.reviewAvg != null ? (
              <button type="button" className={css.shopRating} onClick={scrollToReviews}>
                <span className={css.shopRatingStar}>★</span> {shop.reviewAvg} (
                {shop.reviewCount} {shop.reviewCount === 1 ? 'review' : 'reviews'})
              </button>
            ) : null}
          </div>
          {shop.bio ? <p className={css.shopBio}>{shop.bio}</p> : null}
        </div>
      </section>
      {corporate ? (
        <section className={css.section}>
          <CorporateNote volumePricing={BULK_SHOP_IDS.includes(shopId)} />
        </section>
      ) : null}
      <section className={css.section}>
        <h2 className={css.sectionTitle}>Products ({products.length})</h2>
        <div className={css.productGrid}>
          {products.map(p => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>
      <section className={css.section}>
        <CatalogShopReviews shopId={shopId} />
      </section>
    </main>
  );
};

const ProductContent = ({ productId }) => {
  const [product, setProduct] = useState(null);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getCatalogProduct(productId)
      .then(res => {
        if (cancelled) return;
        setProduct(res.product);
        setShop(res.shop);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [productId]);

  if (loading) return <main className={css.content}><p className={css.loading}>Loading…</p></main>;
  if (!product)
    return <main className={css.content}><p className={css.loading}>Product not found.</p></main>;

  const images = product.images || [];

  return (
    <main className={css.content}>
      <section className={css.section}>
        <div className={css.productLayout}>
          <div className={css.gallery}>
            {images.length > 0 ? (
              <img className={css.galleryMain} src={images[activeImage]} alt={product.title} />
            ) : (
              <div className={css.productImagePlaceholder} />
            )}
            {images.length > 1 ? (
              <div className={css.galleryThumbs}>
                {images.map((url, i) => (
                  <img
                    key={url}
                    src={url}
                    alt=""
                    className={i === activeImage ? css.galleryThumbActive : css.galleryThumb}
                    onClick={() => setActiveImage(i)}
                  />
                ))}
              </div>
            ) : null}
          </div>
          <div className={css.productInfo}>
            <h1 className={css.title}>{product.title}</h1>
            {formatPrice(product.price) ? (
              <p className={css.productPagePrice}>{formatPrice(product.price)}</p>
            ) : null}
            {shop ? (
              <p className={css.soldBy}>
                Sold by{' '}
                <a className={css.shopLink} href={`/store/${shop.id}`}>
                  {shop.publicData?.storeName || shop.displayName}
                </a>
              </p>
            ) : null}
            {product.description ? (
              <p className={css.description}>{product.description}</p>
            ) : null}
            <InquiryForm productId={product.id} />
          </div>
        </div>
      </section>
    </main>
  );
};

const CatalogPage = props => {
  const scrollingDisabled = useSelector(isScrollingDisabled);
  const { mode, params } = props;
  const config = useConfiguration();
  const rootUrl = config?.marketplaceRootURL || 'https://localflower.ca';

  const id = params?.id;
  const isShop = mode === 'shop' && id;
  const isProduct = mode === 'product' && id;

  const meta = isProduct
    ? { title: 'Product | LocalFlower', path: `/item/${id}` }
    : isShop
    ? { title: 'Shop | LocalFlower', path: `/store/${id}` }
    : {
        title: 'Browse Flowers, Cakes & Gifts | LocalFlower',
        path: '/browse',
      };

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={meta.title}
      description="Browse fresh flowers, cakes, balloons, and gifts from local Toronto shops."
      className={css.root}
    >
      <Helmet>
        <link rel="canonical" href={`${rootUrl}${meta.path}`} />
        {/* Parallel catalog preview: keep out of the index until it replaces the main flow */}
        <meta name="robots" content="noindex" />
      </Helmet>
      <LayoutSingleColumn
        className={css.layoutWrapper}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        {isProduct ? (
          <ProductContent productId={id} />
        ) : isShop ? (
          <ShopContent shopId={id} />
        ) : (
          <BrowseContent />
        )}
      </LayoutSingleColumn>
    </Page>
  );
};

CatalogPage.defaultProps = {
  mode: 'browse',
};

CatalogPage.propTypes = {
  mode: string,
};

export default CatalogPage;
